import cloneDeep from 'lodash/cloneDeep';

import { BedragMapper } from './bedrag-mapper';
import { BlobMapper } from './blob-mapper';
import { ContactgegevensMapper } from './contactgegevens-mapper';
import { KalenderMapper } from './kalender-mapper';
import { LabelMapper } from './label-mapper';
import { MultiChoiceMapper } from './multi-choice-mapper';
import { MultiResultaatMapper } from './multi-resultaat-mapper';
import { MultiUploadMapper } from './multi-upload-mapper';
import { NumberMapper } from './number-mapper';
import { SingleChoiceComingDatesMapper } from './single-choice-coming-dates-mapper';
import { SingleChoiceMapper } from './single-choice-mapper';
import { SummaryMapper } from './summary-mapper';
import { TekstMapper } from './tekst-mapper';
import { SelectieMapper } from './selectie-mapper';
import {
  Vragenboom, IndienenVragenboomRequest, VariableContent, Controltype, Vraag, IndienenVragenMailRequest, VraagMail, Vraagtype
} from '@models/claims';
import {
  VraagboomAntwoord, IBaseVraagboomResultaat, VraagboomUploadResultaat, VraagboomDateResultaat, VraagboomTextResultaat,
  VraagboomSelectieResultaat, VraagboomNumberResultaat, VraagboomEmptyResultaat, VraagboomMultiResultaat, VraagboomReparateurResultaat, VraagboomTimeResultaat, VraagboomDateVanTotResultaat,
  Verzekering
} from '@models/vragenboom';
import { SingleChoiceRepairMapper } from './single-choice-repair-mapper';
import { TijdMapper } from './tijd-mapper';
import { KalenderVanTotMapper } from './kalender-van-tot-mapper';

export class IndienenVragenboomRequestMapper {
  static mapAanmaken(polisnummer: string, contractnummer: string, vragenboom: Vragenboom,
    resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]): IndienenVragenboomRequest {
    const request: IndienenVragenboomRequest = new IndienenVragenboomRequest();

    request.contractnummer = contractnummer;
    request.polisnummer = polisnummer;

    if (vragenboom != null && vragenboom.vragenboomMetadata != null) {
      request.vragenboom = new Vragenboom();
      request.vragenboom.vragenboomMetadata = cloneDeep(vragenboom.vragenboomMetadata);
    }

    this.mapResultaten(resultaten, request);
    return request;
  }

  static mapAanmakenMail(vragenboom: Vragenboom, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[], verzekering: Verzekering): IndienenVragenMailRequest {
    const request: IndienenVragenMailRequest = new IndienenVragenMailRequest();
    if (verzekering) {
      request.polisnummer = verzekering.polisnummer;
      request.contractnummer = verzekering.contractnummer;
      request.kenmerken = verzekering.kenmerken;
      request.kenmerken_titel = verzekering.kenmerk_titel;
      request.sleutel = verzekering.sleutel;
    }
    this.mapResultatenMail(resultaten, request);
    return request;
  }

  static mapBijwerken(claimnummer: string, subclaimnummer: string, taaknummer: string, vragenboom: Vragenboom, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]):
    IndienenVragenboomRequest {
    const request: IndienenVragenboomRequest = new IndienenVragenboomRequest();

    request.claimnummer = claimnummer;
    request.subclaimnummer = subclaimnummer;
    request.taaknummer = taaknummer;

    if (vragenboom != null && vragenboom.vragenboomMetadata != null) {
      request.vragenboom = new Vragenboom();
      request.vragenboom.vragenboomMetadata = cloneDeep(vragenboom.vragenboomMetadata);
    }

    this.mapResultaten(resultaten, request);

    return request;
  }

  private static mapResultaten(resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[], request: IndienenVragenboomRequest): void {
    if (resultaten == null || request == null) {
      return;
    }

    const vragen: Vraag[] = [];
    const variableContent: VariableContent[] = [];

    for (const resultaat of resultaten) {
      if (resultaat.vraag && resultaat.vraag.messageVraag) {
        LabelMapper.mapVraag(resultaat.vraag.messageVraag, vragen);
      }

      let controlType = '';
      if (resultaat.vraag != null) {
        controlType = resultaat.vraag.control;
      }

      switch (controlType) {
        case Controltype.Bedrag: {
          BedragMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomNumberResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.Blob: {
          if (resultaat.vraag.vraagtype === Vraagtype.MultiUpload) {
            MultiUploadMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomUploadResultaat>, variableContent, vragen);
          } else {
            BlobMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomUploadResultaat>, variableContent, vragen);
          }
          break;
        }
        case Controltype.Kalender:
        case Controltype.KalenderDmj: {
          KalenderMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomDateResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.KalenderVanTot: {
          KalenderVanTotMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomDateVanTotResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.Tijd: {
          TijdMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomTimeResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.Tekst: {
          TekstMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomTextResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.TekstRegel: {
          TekstMapper.mapResultatenMulti(resultaat as VraagboomAntwoord<VraagboomMultiResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.Decimaal:
        case Controltype.Number:
        case Controltype.SliderSingle: {
          NumberMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomNumberResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.Button:
        case Controltype.SingleChoiceWithDescription:
        case Controltype.SingleChoice: {
          SingleChoiceMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomEmptyResultaat>, vragen);
          break;
        }
        case Controltype.SingleChoiceRepairWithSearch:
        case Controltype.SingleChoiceRepair: {
          SingleChoiceRepairMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomReparateurResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.Referentie:
        case Controltype.Telefoon1:
        case Controltype.Telefoon2:
        case Controltype.Telefoon3: {
          SelectieMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomSelectieResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.SingleChoiceComingDates: {
          SingleChoiceComingDatesMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomEmptyResultaat>, vragen);
          break;
        }
        case Controltype.Label: {
          LabelMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomEmptyResultaat>, vragen);
          break;
        }
        case Controltype.Summary: {
          SummaryMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomTextResultaat>, vragen);
          break;
        }
        case Controltype.ContactGegevens: {
          ContactgegevensMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomMultiResultaat>, variableContent, vragen);
          break;
        }
        case Controltype.MultiChoice: {
          MultiChoiceMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomEmptyResultaat>, vragen);
          break;
        }
        case Controltype.MultiChoiceDoorvragen: {
          MultiResultaatMapper.mapResultaten(resultaat as VraagboomAntwoord<VraagboomMultiResultaat>, variableContent, vragen);
          break;
        }
      }
    }

    for (const vraag of vragen) {
      if (vraag.antwoorden?.length === 0) {
        vraag.antwoorden = undefined;
      }
    }

    request.variabele_content_vragenboom = variableContent;
    request.vragenboom.vragen = vragen;
  }

  private static mapResultatenMail(resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[], request: IndienenVragenMailRequest): void {
    if (resultaten == null || request == null) {
      return;
    }

    const vragen: VraagMail[] = [];

    for (const resultaat of resultaten) {
      let controlType = '';
      if (resultaat.vraag != null) {
        controlType = resultaat.vraag.control;
      }

      switch (controlType) {
        case Controltype.Bedrag: {
          BedragMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomNumberResultaat>, vragen);
          break;
        }
        case Controltype.Blob: {
          if (resultaat.vraag.vraagtype === Vraagtype.MultiUpload) {
            MultiUploadMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomUploadResultaat>, vragen);
          } else {
            BlobMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomUploadResultaat>, vragen);
          }
          break;
        }
        case Controltype.Kalender:
        case Controltype.KalenderDmj: {
          KalenderMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomDateResultaat>, vragen);
          break;
        }
        case Controltype.KalenderVanTot: {
          KalenderVanTotMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomDateVanTotResultaat>, vragen);
          break;
        }
        case Controltype.Tekst: {
          TekstMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomTextResultaat>, vragen);
          break;
        }
        case Controltype.TekstRegel: {
          TekstMapper.mapResultatenMultiMail(resultaat as VraagboomAntwoord<VraagboomMultiResultaat>, vragen);
          break;
        }
        case Controltype.SingleChoiceWithDescription:
        case Controltype.SingleChoice: {
          SingleChoiceMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomEmptyResultaat>, vragen);
          break;
        }
        case Controltype.ContactGegevens: {
          ContactgegevensMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomMultiResultaat>, vragen, request);
          break;
        }
        case Controltype.MultiChoice: {
          MultiChoiceMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomEmptyResultaat>, vragen);
          break;
        }
        case Controltype.Number: {
          NumberMapper.mapResultatenMail(resultaat as VraagboomAntwoord<VraagboomNumberResultaat>, vragen);
          break;
        }
      }
    }

    request.vragen = vragen;
  }
}
